/**
 * 設定ファイル管理
 */

import fs from 'fs';
import path from 'path';
import os from 'os';

export interface AcomoConfig {
  baseUrl: string;
  tenantId: string;
  accessToken: string;
}

const CONFIG_DIR = path.join(os.homedir(), '.acomo');
const CONFIG_FILE = path.join(CONFIG_DIR, 'config.json');

/**
 * 環境変数名の定義
 */
export const ENV_KEYS = {
  baseUrl: 'ACOMO_BASE_URL',
  tenantId: 'ACOMO_TENANT_ID',
  accessToken: 'ACOMO_ACCESS_TOKEN',
} as const;

/** ファイル保存時の baseUrl デフォルト（login で使用。resolveConfig では埋めない） */
export const DEFAULT_BASE_URL = 'https://acomo.app';

/**
 * 環境変数から設定を読み込む（設定されている値のみ）
 */
function loadEnvConfig(): Partial<AcomoConfig> {
  const env: Partial<AcomoConfig> = {};
  if (process.env[ENV_KEYS.baseUrl]) env.baseUrl = process.env[ENV_KEYS.baseUrl]!;
  if (process.env[ENV_KEYS.tenantId]) env.tenantId = process.env[ENV_KEYS.tenantId]!;
  if (process.env[ENV_KEYS.accessToken]) env.accessToken = process.env[ENV_KEYS.accessToken]!;
  return env;
}

/**
 * 設定ファイルを読み込む（baseUrl, tenantId, accessToken のみ返す）
 */
export function loadConfig(): AcomoConfig | null {
  try {
    if (!fs.existsSync(CONFIG_FILE)) {
      return null;
    }
    const content = fs.readFileSync(CONFIG_FILE, 'utf-8');
    const raw = JSON.parse(content) as Record<string, unknown>;
    const baseUrl = typeof raw.baseUrl === 'string' ? raw.baseUrl : '';
    const tenantId = typeof raw.tenantId === 'string' ? raw.tenantId : '';
    const accessToken = typeof raw.accessToken === 'string' ? raw.accessToken : '';
    return { baseUrl, tenantId, accessToken };
  } catch (error) {
    return null;
  }
}

/**
 * 設定ファイルと環境変数をマージして設定を解決する
 * 優先順位: 環境変数 > 設定ファイル
 */
export function resolveConfig(): AcomoConfig | null {
  const fileConfig = loadConfig();
  const envConfig = loadEnvConfig();

  // 環境変数も設定ファイルもない場合
  if (!fileConfig && Object.keys(envConfig).length === 0) {
    return null;
  }

  const merged: AcomoConfig = {
    baseUrl: (envConfig.baseUrl ?? fileConfig?.baseUrl ?? '').trim(),
    tenantId: envConfig.tenantId ?? fileConfig?.tenantId ?? '',
    accessToken: envConfig.accessToken ?? fileConfig?.accessToken ?? '',
  };

  // 必須項目がなければ null
  if (!merged.accessToken || !merged.tenantId) {
    return null;
  }

  return merged;
}

/**
 * 設定ファイルに保存
 */
export function saveConfig(config: AcomoConfig): void {
  // ディレクトリが存在しない場合は作成
  if (!fs.existsSync(CONFIG_DIR)) {
    fs.mkdirSync(CONFIG_DIR, { recursive: true });
  }

  // ファイルに書き込み（パーミッション 0o600）
  fs.writeFileSync(CONFIG_FILE, JSON.stringify(config, null, 2), {
    mode: 0o600,
  });
}

/**
 * 設定ファイルをクリア
 */
export function clearConfig(): void {
  try {
    if (fs.existsSync(CONFIG_FILE)) {
      fs.unlinkSync(CONFIG_FILE);
    }
  } catch (error) {
    // エラーは無視（既に削除されている場合など）
  }
}
